//@ts-ignore
import React, { 
  useCallback, 
  useMemo, 
  useState,
  type ReactNode
} from 'react';
import ElementModal from './ElementModal';
import type { ModalContextType, ModalState } from '../types/modal';

// Create a simple modal manager without using React context
let modalManager: ModalContextType | null = null;

interface ElementModalProviderProps {
  children: ReactNode;
}

// Create a provider component with proper typing
export const ElementModalProvider = ({ children }: ElementModalProviderProps): JSX.Element => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    elementType: null,
    elementProps: {},
  });

  // Open a modal with the specified element
  const openModal = useCallback((elementType: string, elementProps: Record<string, unknown> = {}) => {
    setModalState({
      isOpen: true,
      elementType,
      elementProps,
    });
  }, []);

  // Close the modal
  const closeModal = useCallback(() => {
    setModalState(prevState => ({
      ...prevState,
      isOpen: false,
    }));
  }, []);

  // Render the modal if needed
  const modalContent = useMemo(() => {
    if (!modalState || !modalState.isOpen || !modalState.elementType) {
      return null;
    }

    return (
      <ElementModal
        key={`${modalState.elementType}-${Date.now()}`}
        elementType={modalState.elementType}
        isOpen={modalState.isOpen}
        onClose={closeModal}
      />
    );
  }, [modalState, closeModal]);

  // Create the modal manager value
  const managerValue = useMemo<ModalContextType>(
    () => ({
      openModal,
      closeModal,
      isModalOpen: !!modalState?.isOpen,
    }),
    [openModal, closeModal, modalState?.isOpen]
  );

  // Store the manager for global access
  modalManager = managerValue;

  return (
    <>
      {children}
      {modalContent}
    </>
  );
};

// Function to get the modal manager
export function useElementModal(): ModalContextType {
  if (!modalManager) {
    throw new Error('ElementModalProvider must be mounted before using useElementModal');
  }
  return modalManager;
}

// Export as default for backward compatibility
export default ElementModalProvider;
